import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import styles from './SpecialModeView.module.scss'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import languages from '../languages'
import { celebrate, popScale, shake } from './juice'
import { romanizationOf } from '../speech/flowMatch'
import HandwritingView from './HandwritingView'
import TurkishVerbView, { TR_TENSES } from './TurkishVerbView'
import GermanAdjectiveView, { DEU_PATTERNS } from './GermanAdjectiveView'

import CloseIcon from '@mui/icons-material/Close'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import SkipNextIcon from '@mui/icons-material/SkipNext'
import TableChartIcon from '@mui/icons-material/TableChart'

// Language-specific special modes. Only `ready` ones are runnable; the rest show
// as "coming soon" so the menu communicates the roadmap.
const SPECIAL_MODES = {
  rus: [{ id: 'cases', name: 'Noun Cases', desc: 'Pick the case, then form the ending', ready: true }],
  kor: [
    { id: 'alphabet', name: 'Alphabet (한글)', desc: 'Tap a letter to hear its sound', ready: true },
    { id: 'draw', name: 'Character drawing', desc: 'Hear it, then write the 한글', ready: true },
  ],
  tur: [{ id: 'verbs', name: 'Verb Endings', desc: 'Pick the personal ending, then conjugate', ready: true }],
  deu: [{ id: 'adjectives', name: 'Adjective Endings', desc: 'Weak / mixed / strong, then the ending', ready: true }],
  jpn: [{ id: 'draw', name: 'Kana / Kanji drawing', desc: 'Trace characters by hand', ready: false }],
  cmn: [{ id: 'draw', name: 'Hanzi drawing', desc: 'Trace characters by hand', ready: false }],
}

// Curated common Korean words by syllable count — hand-checked spellings, so the
// handwriting model is always correct (never a model-misspelled Hangul).
const KOR_WORDS = {
  1: [['물', 'water'], ['밥', 'rice'], ['집', 'house'], ['개', 'dog'], ['손', 'hand'], ['눈', 'eye/snow'], ['산', 'mountain'], ['강', 'river'], ['책', 'book'], ['문', 'door'], ['꽃', 'flower'], ['별', 'star'], ['달', 'moon'], ['해', 'sun'], ['비', 'rain'], ['차', 'car/tea'], ['소', 'cow'], ['새', 'bird'], ['발', 'foot'], ['코', 'nose'], ['입', 'mouth'], ['옷', 'clothes'], ['돈', 'money'], ['길', 'road'], ['불', 'fire'], ['곰', 'bear'], ['봄', 'spring'], ['밤', 'night']],
  2: [['사람', 'person'], ['학교', 'school'], ['친구', 'friend'], ['사랑', 'love'], ['음식', 'food'], ['노래', 'song'], ['나라', 'country'], ['시간', 'time'], ['아침', 'morning'], ['저녁', 'evening'], ['나무', 'tree'], ['바다', 'sea'], ['하늘', 'sky'], ['구름', 'cloud'], ['얼굴', 'face'], ['머리', 'head/hair'], ['다리', 'leg/bridge'], ['가방', 'bag'], ['신발', 'shoes'], ['지갑', 'wallet'], ['우유', 'milk'], ['커피', 'coffee'], ['사과', 'apple'], ['딸기', 'strawberry'], ['토끼', 'rabbit'], ['학생', 'student'], ['병원', 'hospital'], ['공원', 'park'], ['시장', 'market'], ['은행', 'bank'], ['우산', 'umbrella'], ['창문', 'window']],
  3: [['고양이', 'cat'], ['바나나', 'banana'], ['도서관', 'library'], ['아버지', 'father'], ['어머니', 'mother'], ['할머니', 'grandmother'], ['자동차', 'automobile'], ['비행기', 'airplane'], ['냉장고', 'refrigerator'], ['컴퓨터', 'computer'], ['화장실', 'bathroom'], ['지하철', 'subway'], ['백화점', 'department store'], ['우체국', 'post office'], ['놀이터', 'playground'], ['무지개', 'rainbow'], ['코끼리', 'elephant'], ['강아지', 'puppy'], ['선생님', 'teacher'], ['젓가락', 'chopsticks'], ['숟가락', 'spoon'], ['이야기', 'story'], ['자전거', 'bicycle'], ['운동화', 'sneakers'], ['도시락', 'lunchbox']],
}

// The Hangul alphabet for the tap-to-hear board. Each tile is [jamo, romanization,
// sample syllable]. We speak the SYLLABLE (가, 아…) because Korean TTS voices a
// full syllable reliably, whereas a bare jamo (ㄱ) is often read as its name.
// Consonants use ㅏ (a); vowels use the silent ㅇ so you hear the vowel alone.
const HANGUL = {
  consonants: [['ㄱ', 'g / k', '가'], ['ㄴ', 'n', '나'], ['ㄷ', 'd / t', '다'], ['ㄹ', 'r / l', '라'], ['ㅁ', 'm', '마'], ['ㅂ', 'b / p', '바'], ['ㅅ', 's', '사'], ['ㅇ', 'ng / –', '아'], ['ㅈ', 'j', '자'], ['ㅊ', 'ch', '차'], ['ㅋ', 'k', '카'], ['ㅌ', 't', '타'], ['ㅍ', 'p', '파'], ['ㅎ', 'h', '하']],
  doubles: [['ㄲ', 'kk', '까'], ['ㄸ', 'tt', '따'], ['ㅃ', 'pp', '빠'], ['ㅆ', 'ss', '싸'], ['ㅉ', 'jj', '짜']],
  vowels: [['ㅏ', 'a', '아'], ['ㅑ', 'ya', '야'], ['ㅓ', 'eo', '어'], ['ㅕ', 'yeo', '여'], ['ㅗ', 'o', '오'], ['ㅛ', 'yo', '요'], ['ㅜ', 'u', '우'], ['ㅠ', 'yu', '유'], ['ㅡ', 'eu', '으'], ['ㅣ', 'i', '이']],
  complexVowels: [['ㅐ', 'ae', '애'], ['ㅒ', 'yae', '얘'], ['ㅔ', 'e', '에'], ['ㅖ', 'ye', '예'], ['ㅘ', 'wa', '와'], ['ㅙ', 'wae', '왜'], ['ㅚ', 'oe', '외'], ['ㅝ', 'wo', '워'], ['ㅞ', 'we', '웨'], ['ㅟ', 'wi', '위'], ['ㅢ', 'ui', '의']],
}

const CASES = [
  {
    key: 'nominative', label: 'Nominative', ru: 'Именительный', q: 'Кто? Что?', qEn: 'Who? What?',
    lines: ['The doer — the person or thing performing the action (the subject).'], ex: [],
  },
  {
    key: 'accusative', label: 'Accusative', ru: 'Винительный', q: 'Кого? Что?', qEn: 'Whom? What?',
    lines: ['The direct object — the person/thing that receives the action.', 'Also the destination when you go somewhere.'], ex: [],
  },
  {
    key: 'genitive', label: 'Genitive', ru: 'Родительный', q: 'Кого? Чего?', qEn: 'Of whom? Of what?',
    lines: ['Possession / "of" something.', 'Absence — after «нет».', 'Quantities.'],
    ex: [{ ru: 'машина моего друга', en: "my friend's car" }, { ru: 'У меня нет машины', en: "I don't have a car" }],
  },
  {
    key: 'prepositional', label: 'Prepositional', ru: 'Предложный', q: 'О ком? О чём?', qEn: 'About whom/what? · Where?',
    lines: ['Always used WITH a preposition.', 'Location (where?).', 'Talking / thinking about something.'],
    ex: [{ ru: 'Я живу в Москве', en: 'I live in Moscow' }, { ru: 'Я говорю о работе', en: 'I am talking about work' }],
  },
  {
    key: 'dative', label: 'Dative', ru: 'Дательный', q: 'Кому? Чему?', qEn: 'To whom? To what?',
    lines: ['Something given, said, sent or done TO someone.', 'Use with «по».'], ex: [],
  },
  {
    key: 'instrumental', label: 'Instrumental', ru: 'Творительный', q: 'Кем? Чем?', qEn: 'With whom? With what?',
    lines: ['Mainly with «с» = with.', 'Also profession / being something.'],
    ex: [{ ru: 'Он работает врачом', en: 'He works as a doctor' }],
  },
]
const CASE_BY_KEY = Object.fromEntries(CASES.map(c => [c.key, c]))

// Condensed declension table (adjective + noun endings + examples) for the
// guided reference board.
const DECL = {
  nominative: [
    { g: ['m'], adj: '-ый (-ий), -ой', noun: '— (zero)', ex: 'новый хороший студент, большой словарь' },
    { g: ['n'], adj: '-ое (-ее)', noun: '-о (-е, -ё)', ex: 'первое слово, синее платье' },
    { g: ['f'], adj: '-ая (-яя)', noun: '-а (-я)', ex: 'новая студентка, синяя тетрадь, Россия' },
    { num: 'pl', adj: '-ые (-ие)', noun: '-ы (-и) · n. -а (-я)', ex: 'новые студенты, словари, синие тетради' },
  ],
  accusative: [
    { g: ['m'], adj: '= Nom / = Gen', noun: '= Nom / = Gen', ex: 'новый учебник · нового студента' },
    { g: ['n'], adj: '= Nom', noun: '= Nom', ex: 'первое слово, синее платье' },
    { g: ['f'], adj: '-ую (-юю)', noun: '-у (-ю)', ex: 'новую студентку, синюю тетрадь, Россию' },
    { num: 'pl', adj: '= Nom / = Gen', noun: '= Nom / = Gen', ex: 'новые газеты · новых студентов' },
  ],
  genitive: [
    { g: ['m', 'n'], adj: '-ого (-его)', noun: '-а (-я)', ex: 'нового студента, словаря; синего платья' },
    { g: ['f'], adj: '-ой (-ей)', noun: '-ы (-и)', ex: 'новой студентки, синей тетради, России' },
    { num: 'pl', adj: '-ых (-их)', noun: '-ов (-ев, -ей) · -Ø, *-ей', ex: 'новых студентов, словарей; тетрадей' },
  ],
  prepositional: [
    { g: ['m', 'n'], adj: '-ом (-ем)', noun: '-е, *-и', ex: 'о новом студенте, словаре; о синем платье' },
    { g: ['f'], adj: '-ой (-ей)', noun: '-е, *-и', ex: 'о новой студентке, синей тетради, России' },
    { num: 'pl', adj: '-ых (-их)', noun: '-ах (-ях)', ex: 'о новых студентах, словарях, платьях' },
  ],
  dative: [
    { g: ['m', 'n'], adj: '-ому (-ему)', noun: '-у (-ю)', ex: 'новому студенту, словарю; синему платью' },
    { g: ['f'], adj: '-ой (-ей)', noun: '-е, *-и', ex: 'новой студентке, синей тетради, России' },
    { num: 'pl', adj: '-ым (-им)', noun: '-ам (-ям)', ex: 'новым студентам, словам, платьям' },
  ],
  instrumental: [
    { g: ['m', 'n'], adj: '-ым (-им)', noun: '-ом (-ем/ём)', ex: 'новым студентом, словарём; синим платьем' },
    { g: ['f'], adj: '-ой (-ей)', noun: '-ой (-ей), *-ю', ex: 'новой студенткой, Россией, тетрадью' },
    { num: 'pl', adj: '-ыми (-ими)', noun: '-ами (-ями)', ex: 'новыми студентами, словарями, платьями' },
  ],
}

const normGender = (g, noun) => {
  const s = String(g || '').toLowerCase()
  if (/^m|masc|муж/.test(s)) return 'm'
  if (/^f|fem|жен/.test(s)) return 'f'
  if (/^n|neut|сред/.test(s)) return 'n'
  const n = String(noun || '').trim().toLowerCase()
  if (/[ая]$/.test(n)) return 'f'
  if (/[оеё]$/.test(n)) return 'n'
  return 'm'
}

// Russian case selection is highly rule-based — the model was guessing (к →
// "accusative"). Handing it the triggers makes it far more reliable.
const RU_CASE_RULES = `Russian case triggers — apply STRICTLY (the case MUST match the trigger):
- к, по → DATIVE. Also the indirect recipient ("to/for someone") → DATIVE.
- о, об, при → PREPOSITIONAL. в/на meaning LOCATION ("where?") → PREPOSITIONAL.
- в, на, за, под, через (motion INTO/onto, "where to?") → ACCUSATIVE. A direct object of a transitive verb → ACCUSATIVE.
- из, от, до, для, без, у, около, после, против, вокруг, кроме → GENITIVE. "нет / не было" (there isn't) → GENITIVE. Possession / quantity / "of" → GENITIVE.
- с meaning "with" → INSTRUMENTAL. перед, над, под, за (location), между → INSTRUMENTAL. с meaning "from/off" → GENITIVE.
- The subject → NOMINATIVE.`

const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '').trim()

const parseItems = (raw) => {
  if (!raw) return []
  let t = String(raw).replace(/```json/gi, '').replace(/```/g, '').trim()
  const a = t.indexOf('{'), b = t.lastIndexOf('}')
  if (a !== -1 && b !== -1) t = t.slice(a, b + 1)
  try { const o = JSON.parse(t); return Array.isArray(o?.items) ? o.items : [] } catch { return [] }
}

const shapeCaseItem = (raw) => {
  const answer = String(raw?.answer || '').trim()
  const baseNoun = String(raw?.baseNoun || '').trim()
  const caseKey = String(raw?.case || '').trim().toLowerCase()
  if (!answer || !baseNoun || !CASE_BY_KEY[caseKey]) return null
  // Derive the visible stem + the case ending from the noun itself — the part
  // of the inflected form that differs from the dictionary form (учитель →
  // учител + ю). Always correct and always in Cyrillic; never the model's guessy
  // English transliteration ("-y").
  const A = baseNoun.toLowerCase(), B = answer.toLowerCase()
  let cp = 0
  while (cp < Math.min(A.length, B.length) && A[cp] === B[cp]) cp++
  let stem = answer.slice(0, cp)
  let ending = answer.slice(cp)
  if (cp === 0) { stem = ''; ending = answer }   // fully irregular — reveal whole word
  // Split the sentence around the target noun for display.
  const template = String(raw?.sentenceTemplate || '').trim()
  const sentence = String(raw?.sentence || '').trim()
  let prefix = '', suffix = ''
  const parts = template.split(/⟦[^⟧]*⟧|_{2,}|\{[^}]*\}/)
  if (template && parts.length >= 2) { prefix = parts[0]; suffix = parts.slice(1).join(' ') }
  else if (sentence) {
    const i = sentence.toLowerCase().indexOf(answer.toLowerCase())
    if (i >= 0) { prefix = sentence.slice(0, i); suffix = sentence.slice(i + answer.length) }
    else { prefix = sentence + ' '; suffix = '' }
  }
  return {
    baseNoun, caseKey, answer, stem, ending,
    gender: normGender(raw?.gender, baseNoun),
    number: /plural|pl|мн/i.test(String(raw?.number || '')) ? 'pl' : 'sg',
    prefix: prefix.trim(), suffix: suffix.trim(),
    sentence: sentence || `${prefix}${answer}${suffix}`,
    english: String(raw?.english || '').trim(),
    why: String(raw?.why || '').trim(),
    endingHint: String(raw?.endingHint || '').trim(),
  }
}

export default function SpecialModeView({ voiceSpeed, onClose }) {
  const [lang, setLang] = useState('rus')
  const [mode, setMode] = useState('cases')
  const [query, setQuery] = useState('')
  const [count, setCount] = useState(10)
  const [drawLen, setDrawLen] = useState('1')     // '1' | '2' | '3' (up to N syllables)
  const [drawCards, setDrawCards] = useState([])
  const [trTense, setTrTense] = useState('present') // Turkish verb-endings: one tense at a time
  const [deuNumber, setDeuNumber] = useState('mixed')  // German adjectives: sg | pl | mixed
  const [deuPattern, setDeuPattern] = useState('all')  // all | weak | mixed | strong
  const [phase, setPhase] = useState('setup')   // setup | loading | drill | error
  const [errorMsg, setErrorMsg] = useState('')
  const [regenerating, setRegenerating] = useState(false)

  const [items, setItems] = useState([])
  const [idx, setIdx] = useState(0)
  const [setNum, setSetNum] = useState(1)
  const [stage, setStage] = useState('case')     // case | ending | done
  const [caseChoice, setCaseChoice] = useState(null)
  const [caseResult, setCaseResult] = useState(null) // right | wrong | revealed
  const [caseAttempts, setCaseAttempts] = useState(0)
  const [typed, setTyped] = useState('')
  const [endAttempts, setEndAttempts] = useState(0)
  const [endRevealed, setEndRevealed] = useState(false)
  const [combo, setCombo] = useState(0)
  const [best, setBest] = useState(0)
  const [explainText, setExplainText] = useState(null)
  const [explainLoading, setExplainLoading] = useState(false)
  const [showExplain, setShowExplain] = useState(false)
  const [caseHintShort, setCaseHintShort] = useState(null)
  const [caseHintLong, setCaseHintLong] = useState(null)
  const [caseHintLoading, setCaseHintLoading] = useState(false)
  const [showCaseHint, setShowCaseHint] = useState(false)
  const [hintMode, setHintMode] = useState('short')
  const [showRef, setShowRef] = useState(false)  // guided endings reference board

  const item = items[idx]
  const advanceTimerRef = useRef(null)
  const inputRef = useRef(null)
  const cardRef = useRef(null)
  const comboRef = useRef(null)
  const caseRowRef = useRef(null)
  const completedRef = useRef(false)

  const serverUrl = process.env.REACT_APP_OLLAMA_URI
  const apiKey = process.env.REACT_APP_OLLAMA_KEY
  const langName = languages[lang] || lang
  const modesForLang = SPECIAL_MODES[lang] || []
  const activeMode = modesForLang.find(m => m.id === mode)

  useEffect(() => { if (combo > 0) popScale(comboRef.current) }, [combo])
  // Default the mode to the first ready one when the language changes.
  useEffect(() => {
    const ready = (SPECIAL_MODES[lang] || []).find(m => m.ready)
    setMode(ready ? ready.id : ((SPECIAL_MODES[lang] || [])[0]?.id || ''))
  }, [lang])

  // ── generation ──────────────────────────────────────────────────────────
  const generate = useCallback(async (isRegen = false) => {
    if (!serverUrl) { setErrorMsg('Mac Studio (REACT_APP_OLLAMA_URI) is not configured.'); setPhase('error'); return }
    if (isRegen) setRegenerating(true); else setPhase('loading')
    setErrorMsg('')

    const prompt = `You are a Russian teacher creating a NOUN-CASE practice set (textbook fill-in-the-blank style).
${query.trim() ? `Focus: "${query.trim()}".` : 'Use a spread across all six cases (nominative, accusative, genitive, dative, instrumental, prepositional).'}

Create ${count} items. In each, exactly ONE noun must be put into the correct case; the rest of the sentence is already correct and natural. Prefer common, beginner-friendly nouns.

${RU_CASE_RULES}
CRITICAL: the "case" you assign MUST be the one the trigger in the sentence actually requires (check the preposition/role against the rules above), and "answer" MUST be the correctly inflected form. Do NOT leave the noun unchanged unless its form in that case genuinely equals the dictionary form. If unsure how a noun declines, pick an easier, very common noun you are certain about.

For each item provide:
- baseNoun: the target noun in its dictionary form (nominative singular)
- gender: the noun's gender — "m", "f" or "n"
- number: "singular" or "plural" (the form needed in the sentence)
- case: one of "nominative","accusative","genitive","dative","instrumental","prepositional"
- answer: the noun correctly inflected into that case (exactly the word that goes in the blank)
- stem: the unchanging leading part of "answer" (may be empty)
- ending: the rest, so that stem + ending EXACTLY equals answer
- sentenceTemplate: the full Russian sentence but with the target noun replaced by the token ⟦N⟧
- english: the English translation of the sentence
- why: ONE short English sentence explaining WHY this case is used (the trigger)
- endingHint: ONE short English hint for forming the ending — do NOT give the full answer

Return ONLY minified JSON, no markdown:
{"items":[{"baseNoun":"","gender":"","number":"","case":"","answer":"","stem":"","ending":"","sentenceTemplate":"","english":"","why":"","endingHint":""}]}`

    try {
      const res = await fetch(`${serverUrl}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        body: JSON.stringify({ model: 'gemma3:27b', prompt, stream: false, format: 'json', options: { temperature: 0.3, num_predict: 2600 } }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const built = parseItems(data.response).map(shapeCaseItem).filter(Boolean)
      if (built.length === 0) { setErrorMsg('The model returned no usable items — try again.'); setPhase('error'); setRegenerating(false); return }

      // ── grammar-check & correct pass ──────────────────────────────────────
      // Russian case + inflection is exactly where the model slips (к → dative,
      // not accusative). Re-verify every item against the rules and fix it
      // before the learner ever sees it. Best-effort: keep drafts if it fails.
      let final = built
      try {
        setRegenerating(true)
        const list = built.map((d, i) => `${i + 1}. "${d.prefix}⟦${d.baseNoun}⟧${d.suffix}" — draft says case=${d.caseKey}, answer=${d.answer}`).join('\n')
        const vres = await fetch(`${serverUrl}/api/generate`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
          body: JSON.stringify({
            model: 'gemma3:27b', stream: false, format: 'json', options: { temperature: 0.15, num_predict: 2400 },
            prompt: `You are a strict Russian grammar checker. Some drafts have the WRONG case or a wrong inflected form — correct every one.
${RU_CASE_RULES}
Items (⟦baseNoun⟧ is the dictionary form of the noun that must be inflected for the sentence):
${list}
Return ONLY JSON {"items":[{"case":"","answer":"","stem":"","ending":"","why":"","endingHint":""}]} with EXACTLY ${built.length} items in the SAME order. "case" = the case the sentence truly requires; "answer" = baseNoun correctly inflected into that case; stem + ending = answer; "why" = one short English sentence naming the trigger.`,
          }),
        })
        if (vres.ok) {
          const arr = parseItems((await vres.json()).response)
          if (arr.length === built.length) {
            const fixed = built.map((d, i) => shapeCaseItem({
              baseNoun: d.baseNoun, sentenceTemplate: `${d.prefix}⟦N⟧${d.suffix}`, english: d.english,
              gender: d.gender, number: d.number,
              case: arr[i]?.case || d.caseKey, answer: arr[i]?.answer || d.answer,
              stem: arr[i]?.stem || '', ending: arr[i]?.ending || '',
              why: arr[i]?.why || d.why, endingHint: arr[i]?.endingHint || d.endingHint,
            })).filter(Boolean)
            if (fixed.length) final = fixed
          }
        }
      } catch { /* keep drafts */ }

      setItems(final); setIdx(0); setSetNum(n => (isRegen ? n + 1 : 1)); setRegenerating(false); setPhase('drill')
    } catch (e) {
      setErrorMsg((e.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the Mac Studio failed — check the tunnel / Ollama.' : (e.message || 'Generation failed.'))
      setPhase('error'); setRegenerating(false)
    }
  }, [serverUrl, apiKey, query, count])

  // Korean handwriting: build a deck from the curated word list (correct
  // spellings), then hand it to the shared handwriting board with auto-speak on.
  const startDraw = () => {
    const buckets = drawLen === '1' ? [1] : drawLen === '2' ? [1, 2] : [1, 2, 3]
    const pool = buckets.flatMap(n => (KOR_WORDS[n] || []).map(([k, e]) => ({ k, e })))
    const cards = pool.sort(() => Math.random() - 0.5).slice(0, count).map(w => ({
      sourceId: w.k, source: w.k, sourceLanguage: 'kor',
      translations: [{ targetId: `${w.k}-e`, target: w.e, targetLanguage: 'eng' }],
    }))
    setDrawCards(cards)
    setPhase('drill')
  }
  const start = () => {
    if (mode === 'draw') return startDraw()
    if (mode === 'alphabet') return setPhase('drill') // static board, no generation
    if (mode === 'verbs') return setPhase('drill') // TurkishVerbView generates itself
    if (mode === 'adjectives') return setPhase('drill') // GermanAdjectiveView builds locally
    return generate(false)
  }

  // Reset per item.
  useEffect(() => {
    if (phase !== 'drill' || !item) return
    setStage('case'); setCaseChoice(null); setCaseResult(null); setCaseAttempts(0)
    setTyped(''); setEndAttempts(0); setEndRevealed(false)
    setExplainText(null); setExplainLoading(false); setShowExplain(false)
    setCaseHintShort(null); setCaseHintLong(null); setCaseHintLoading(false); setShowCaseHint(false); setHintMode('short')
    completedRef.current = false
  }, [idx, phase, item])

  useEffect(() => { if (stage === 'ending') setTimeout(() => inputRef.current?.focus(), 60) }, [stage])

  const advance = useCallback(() => {
    clearTimeout(advanceTimerRef.current)
    setIdx(prev => {
      const next = prev + 1
      if (next >= items.length) { generate(true); return prev }
      return next
    })
  }, [items.length, generate])

  const completeItem = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true
    setStage('done')
    const el = cardRef.current
    if (el) { const r = el.getBoundingClientRect(); celebrate(r.left + r.width / 2, r.top + r.height / 2, { count: 28, spread: 170 }) }
    setCombo(c => { const nc = c + 1; setBest(b => Math.max(b, nc)); if (nc % 5 === 0) celebrate(window.innerWidth / 2, window.innerHeight / 2.4, { count: 64, spread: 260 }); return nc })
    advanceTimerRef.current = setTimeout(advance, 900)
  }, [advance])

  const pickCase = (key) => {
    if (!item || caseResult === 'right' || caseResult === 'revealed') return
    setCaseChoice(key)
    if (key === item.caseKey) {
      setCaseResult('right')
      setTimeout(() => setStage('ending'), 750)
    } else {
      const n = caseAttempts + 1
      setCaseAttempts(n)
      setCaseResult('wrong')
      shake(caseRowRef.current)
      if (n >= 2) { setCaseChoice(item.caseKey); setCaseResult('revealed') }   // wait for the learner to tap Continue
    }
  }

  const isEndingCorrect = (val) => {
    const v = norm(val)
    return v === norm(item.answer) || norm((item.stem || '') + val) === norm(item.answer)
  }
  const onTyped = (val) => {
    setTyped(val)
    if (item && isEndingCorrect(val)) completeItem()
  }
  const checkEnding = () => {
    if (!item || completedRef.current) return
    if (isEndingCorrect(typed)) { completeItem(); return }
    const n = endAttempts + 1
    setEndAttempts(n)
    shake(inputRef.current)
    if (n >= 5) setEndRevealed(true)
  }
  const skip = () => { setCombo(0); advance() }
  const flagWrong = () => { advance() } // skip a dubious item WITHOUT breaking the streak
  const hear = () => { if (item) speak(item.sentence, voices[lang] || voices['eng'], voiceSpeed) }
  const proceedToEnding = () => setStage('ending')

  // On-demand deeper explanation of why this case applies (3-4 lines + an example).
  const explainMore = useCallback(async () => {
    if (showExplain) { setShowExplain(false); return }
    setShowExplain(true)
    if (explainText || explainLoading || !item) return
    setExplainLoading(true)
    try {
      const caseLabel = CASE_BY_KEY[item.caseKey]?.label
      const p = `You are a friendly Russian tutor. In 3-4 very short lines, explain clearly WHY this sentence puts the noun in the ${caseLabel} case.
Sentence: ${item.sentence}
Noun: ${item.baseNoun} → ${item.answer} (${caseLabel})
Name the exact trigger (the preposition or the grammatical role). Then give ONE more short example sentence that uses the ${caseLabel} for the same reason. Plain English, no markdown, no preamble.`
      const res = await fetch(`${serverUrl}/api/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey }, body: JSON.stringify({ model: 'gemma3:27b', prompt: p, stream: false, options: { temperature: 0.4, num_predict: 260 } }) })
      if (!res.ok) throw new Error()
      const data = await res.json()
      setExplainText((data.response || '').trim() || '—')
    } catch { setExplainText('Could not reach the Mac Studio for more detail.') }
    setExplainLoading(false)
  }, [showExplain, explainText, explainLoading, item, serverUrl, apiKey])

  // Strip any greeting/filler the model prepends despite instructions.
  const cleanHint = (s) => {
    let t = String(s || '').trim()
    t = t.replace(/^((ah|oh)[,!.\s]*)?(hello|hi|hey)\b[^.!?]*[.!?]\s*/i, '')
    t = t.replace(/^\s*(sure|okay|ok|alright|great|of course)\b[^.!?]*[.!?]\s*/i, '')
    t = t.replace(/^\s*let'?s\b[^.!?]*[.!?]\s*/i, '')
    return t.trim() || '—'
  }

  const fetchHint = useCallback(async (long) => {
    if (!item) return
    if (long && caseHintLong) { setHintMode('long'); return }
    if (!long && caseHintShort) { setHintMode('short'); return }
    setHintMode(long ? 'long' : 'short')
    setCaseHintLoading(true)
    const ctx = `The learner must choose the correct case for the bracketed noun. Point them at what to look at (the preposition right before the noun, the verb, or the noun's role: doer / receiver / possessed / tool "with" / location). Rules: NO greeting, no "let's"/"together"/"okay", do NOT name the case, do NOT give the inflected form.
Sentence: ${item.prefix} ⟦${item.baseNoun}⟧ ${item.suffix}
English: ${item.english}`
    const p = long
      ? `You are a concise Russian tutor. In 3-4 short sentences, guide the learner: what to notice, why it matters, and one plain-English analogy. ${ctx}`
      : `You are a concise Russian tutor. In 1-2 short sentences, straight to the point, tell the learner what to look at. ${ctx}`
    try {
      const res = await fetch(`${serverUrl}/api/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey }, body: JSON.stringify({ model: 'gemma3:27b', prompt: p, stream: false, options: { temperature: 0.4, num_predict: long ? 240 : 90 } }) })
      if (!res.ok) throw new Error()
      const data = await res.json()
      const txt = cleanHint(data.response)
      if (long) setCaseHintLong(txt); else setCaseHintShort(txt)
    } catch {
      const err = 'Could not reach the Mac Studio for a hint.'
      if (long) setCaseHintLong(err); else setCaseHintShort(err)
    }
    setCaseHintLoading(false)
  }, [item, caseHintShort, caseHintLong, serverUrl, apiKey])

  // The 💡 button: open/close; on open, fetch the short hint by default.
  const caseHint = useCallback(() => {
    if (showCaseHint) { setShowCaseHint(false); return }
    setShowCaseHint(true); setHintMode('short')
    if (!caseHintShort && !caseHintLoading) fetchHint(false)
  }, [showCaseHint, caseHintShort, caseHintLoading, fetchHint])

  // ── setup ──────────────────────────────────────────────────────────────────
  if (phase !== 'drill') {
    return (
      <div className={styles.view}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.panel}>
          <div className={styles.modeLabel}>Special Mode</div>
          <h1 className={styles.title}>Language-specific practice</h1>
          <p className={styles.sub}>Pick a language, then a mode built for its hardest bits.</p>

          {phase === 'loading' ? (
            <div className={styles.loading}><div className={styles.spinner} /><div className={styles.loadingText}>Building {count} exercises…</div></div>
          ) : (
            <>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Language</span>
                <select className={styles.select} value={lang} onChange={e => setLang(e.target.value)}>
                  {Object.keys(SPECIAL_MODES).map(code => <option key={code} value={code}>{languages[code] || code}</option>)}
                </select>
              </div>

              <div className={styles.field}>
                <span className={styles.fieldLabel}>Mode</span>
                <div className={styles.modeGrid}>
                  {modesForLang.map(m => (
                    <button key={m.id} disabled={!m.ready} className={`${styles.modeCard} ${mode === m.id ? styles.modeCardActive : ''} ${!m.ready ? styles.modeCardSoon : ''}`} onClick={() => m.ready && setMode(m.id)}>
                      <span className={styles.modeName}>{m.name}{!m.ready && ' · soon'}</span>
                      <span className={styles.modeDesc}>{m.desc}</span>
                    </button>
                  ))}
                  {modesForLang.length === 0 && <div className={styles.modeDesc}>No special modes for this language yet.</div>}
                </div>
              </div>

              {activeMode?.ready && mode === 'cases' && (
                <>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Focus (optional)</span>
                    <textarea className={styles.queryBox} value={query} onChange={e => setQuery(e.target.value)} rows={2}
                      placeholder={'Leave blank for all six cases, or focus it —\ne.g. "genitive and dative", "prepositional case with places"'} />
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>How many</span>
                    <div className={styles.chipRow}>{[5, 10, 15].map(n => <button key={n} className={`${styles.chip} ${count === n ? styles.chipActive : ''}`} onClick={() => setCount(n)}>{n}</button>)}</div>
                  </div>
                </>
              )}

              {activeMode?.ready && mode === 'verbs' && (
                <>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Tense (one at a time)</span>
                    <div className={styles.chipRow}>
                      {TR_TENSES.map(t => (
                        <button key={t.key} className={`${styles.chip} ${trTense === t.key ? styles.chipActive : ''}`} onClick={() => setTrTense(t.key)}>{t.label}</button>
                      ))}
                    </div>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Focus (optional)</span>
                    <textarea className={styles.queryBox} value={query} onChange={e => setQuery(e.target.value)} rows={2}
                      placeholder={'Leave blank for common verbs, or focus it —\ne.g. "gelmek, gitmek, yapmak", "everyday verbs"'} />
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>How many</span>
                    <div className={styles.chipRow}>{[5, 10, 15].map(n => <button key={n} className={`${styles.chip} ${count === n ? styles.chipActive : ''}`} onClick={() => setCount(n)}>{n}</button>)}</div>
                  </div>
                </>
              )}

              {activeMode?.ready && mode === 'adjectives' && (
                <>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>The three patterns</span>
                    <div className={styles.teachBox}>
                      {DEU_PATTERNS.map(p => (
                        <div key={p.key} className={styles.teachLine}>
                          <strong>{p.name}</strong> — {p.trigger}. <span className={styles.teachDim}>{p.why}</span>
                        </div>
                      ))}
                      <div className={styles.teachTip}>You choose the pattern, then the ending. Open the ▦ tables any time — they guide you to the exact cell.</div>
                    </div>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Number</span>
                    <div className={styles.chipRow}>
                      {[['mixed', 'Mixed'], ['sg', 'Singular'], ['pl', 'Plural']].map(([v, l]) => (
                        <button key={v} className={`${styles.chip} ${deuNumber === v ? styles.chipActive : ''}`} onClick={() => setDeuNumber(v)}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Focus pattern</span>
                    <div className={styles.chipRow}>
                      {[['all', 'All three'], ['weak', 'Weak'], ['mixed', 'Mixed'], ['strong', 'Strong']].map(([v, l]) => (
                        <button key={v} className={`${styles.chip} ${deuPattern === v ? styles.chipActive : ''}`} onClick={() => setDeuPattern(v)}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>How many</span>
                    <div className={styles.chipRow}>{[5, 10, 15].map(n => <button key={n} className={`${styles.chip} ${count === n ? styles.chipActive : ''}`} onClick={() => setCount(n)}>{n}</button>)}</div>
                  </div>
                </>
              )}

              {activeMode?.ready && mode === 'draw' && (
                <>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Characters</span>
                    <div className={styles.chipRow}>
                      {[['1', 'Single'], ['2', 'Up to 2'], ['3', 'Up to 3']].map(([v, l]) => (
                        <button key={v} className={`${styles.chip} ${drawLen === v ? styles.chipActive : ''}`} onClick={() => setDrawLen(v)}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>How many words</span>
                    <div className={styles.chipRow}>{[5, 10, 15].map(n => <button key={n} className={`${styles.chip} ${count === n ? styles.chipActive : ''}`} onClick={() => setCount(n)}>{n}</button>)}</div>
                  </div>
                </>
              )}

              {phase === 'error' && <div className={styles.error}>{errorMsg}</div>}
              <button className={styles.generateBtn} onClick={start} disabled={!activeMode?.ready}>✦ Start practice</button>
            </>
          )}
        </div>
      </div>
    )
  }

  // ── Korean alphabet: tap-to-hear board ──────────────────────────────────────
  if (mode === 'alphabet') {
    return <HangulBoard voiceSpeed={voiceSpeed} onClose={() => setPhase('setup')} />
  }

  // ── Korean handwriting: hand off to the shared board (hear → write) ─────────
  if (mode === 'draw') {
    return <HandwritingView cards={drawCards} autoSpeak checkDrawing voiceSpeed={voiceSpeed} onClose={() => setPhase('setup')} />
  }

  // ── Turkish verb endings: hand off to its own two-stage drill ───────────────
  if (mode === 'verbs') {
    return <TurkishVerbView tenseKey={trTense} count={count} query={query} voiceSpeed={voiceSpeed} onBack={() => setPhase('setup')} onClose={onClose} />
  }

  // ── German adjective endings: hand off to its own two-stage drill ───────────
  if (mode === 'adjectives') {
    return <GermanAdjectiveView count={count} patternFocus={deuPattern} numberMode={deuNumber} voiceSpeed={voiceSpeed} onBack={() => setPhase('setup')} onClose={onClose} />
  }

  // ── drill (Russian cases) ───────────────────────────────────────────────────
  const roman = romanizationOf(item?.sentence, lang)
  const caseObj = CASE_BY_KEY[item?.caseKey]
  const explainPanel = showExplain ? (
    <div className={styles.explainPanel}>{explainLoading ? <span className={styles.dots}>thinking…</span> : (explainText || '')}</div>
  ) : null
  const revealLetters = endRevealed ? (item?.ending || '').length : Math.max(0, Math.min((item?.ending || '').length, endAttempts - 1))
  const endingScaffold = (item?.stem || '') + (item?.ending || '').slice(0, revealLetters)
  const endingBlanks = '_'.repeat(Math.max(0, (item?.ending || '').length - revealLetters))

  return (
    <div className={styles.view}>
      <button className={styles.closeBtn} onClick={() => setPhase('setup')}><CloseIcon /></button>
      {regenerating && <div className={styles.regenOverlay}><div className={styles.spinner} /><div className={styles.loadingText}>New set…</div></div>}

      <div className={styles.drill}>
        <div className={styles.drillTop}>
          <span className={styles.modeLabel}>Set {setNum} · Cases · Russian</span>
          <div className={styles.comboWrap}><span className={styles.comboFire}>{combo >= 3 ? '🔥' : '✦'}</span><span className={styles.combo} ref={comboRef}>{combo}</span></div>
        </div>

        <div className={styles.card} key={idx} ref={cardRef}>
          <div className={styles.sentenceLine}>
            <span className={styles.ctx}>{item?.prefix}</span>
            <span className={styles.cue}>({item?.baseNoun})</span>
            {stage === 'done'
              ? <span className={styles.answerGot}>{item?.answer}</span>
              : <span className={styles.slot}>{caseObj && (caseResult === 'right' || caseResult === 'revealed' || stage !== 'case') ? caseObj.label.slice(0, 3) + '.' : '···'}</span>}
            <span className={styles.ctx}>{item?.suffix}</span>
          </div>
          {item?.english && <div className={styles.english}>{item.english}</div>}
          {roman && <div className={styles.roman}>{roman}</div>}

          {/* stage 1: pick the case */}
          {stage === 'case' && (
            <>
              <div className={styles.stageLabel}>Which case does <strong>{item?.baseNoun}</strong> take here?</div>
              <div className={styles.caseGrid} ref={caseRowRef}>
                {CASES.map(c => {
                  const chosen = caseChoice === c.key
                  const isAnswer = item?.caseKey === c.key
                  const cls = [
                    styles.caseBtn,
                    caseResult && isAnswer && (caseResult === 'right' || caseResult === 'revealed') ? styles.caseRight : '',
                    chosen && caseResult === 'wrong' && !isAnswer ? styles.caseWrong : '',
                  ].join(' ')
                  return (
                    <div key={c.key} className={styles.caseCell}>
                      <button className={cls} onClick={() => pickCase(c.key)}>
                        <span className={styles.caseName}>{c.label}</span>
                        <span className={styles.caseRu}>{c.ru}</span>
                        <span className={styles.caseQ}>{c.q}</span>
                      </button>
                      <div className={styles.caseInfo}>
                        <span className={styles.caseInfoHead}>{c.label} · {c.ru}</span>
                        <span className={styles.caseInfoQ}>{c.qEn} · {c.q}</span>
                        {c.lines.map((l, i) => <span key={i} className={styles.caseInfoLine}>{l}</span>)}
                        {c.ex.map((e, i) => <span key={`e${i}`} className={styles.caseInfoEx}><b>{e.ru}</b> — {e.en}</span>)}
                      </div>
                    </div>
                  )
                })}
              </div>
              <button className={styles.hintBtn} onClick={caseHint}>{showCaseHint ? 'hide hint' : '💡 What should I notice?'}</button>
              {showCaseHint && (
                <div className={styles.hintPanel}>
                  {caseHintLoading
                    ? <span className={styles.dots}>thinking…</span>
                    : (hintMode === 'long' ? caseHintLong : caseHintShort) || ''}
                  {!caseHintLoading && (
                    <div className={styles.hintMoreRow}>
                      {hintMode === 'short'
                        ? <button className={styles.linkBtn} onClick={() => fetchHint(true)}>more detail →</button>
                        : <button className={styles.linkBtn} onClick={() => setHintMode('short')}>← shorter</button>}
                    </div>
                  )}
                </div>
              )}

              {caseResult === 'wrong' && caseAttempts < 2 && (
                <div className={styles.nudge}>Not that one — check the hint above, then try again.</div>
              )}
              {caseResult === 'revealed' && (
                <>
                  <div className={styles.whyBox}>
                    It's <strong>{caseObj?.label}</strong> ({caseObj?.ru}). {item?.why}
                    {' '}<button className={styles.explainLink} onClick={explainMore}>{showExplain ? 'less' : 'explain more'}</button>
                  </div>
                  {explainPanel}
                  <button className={styles.continueBtn} onClick={proceedToEnding}>Continue →</button>
                </>
              )}
            </>
          )}

          {/* stage 2: form the ending */}
          {stage === 'ending' && (
            <>
              <div className={styles.whyBox}>
                <strong>{caseObj?.label}</strong> ✓ — {item?.why}
                {' '}<button className={styles.explainLink} onClick={explainMore}>{showExplain ? 'less' : 'explain more'}</button>
              </div>
              {explainPanel}
              <div className={styles.stageLabel}>Now write <strong>{item?.baseNoun}</strong> in the {caseObj?.label}:</div>
              <div className={styles.endingScaffold}>
                <span className={styles.stem}>{endingScaffold}</span><span className={styles.blankInline}>{endingBlanks}</span>
              </div>
              <input
                ref={inputRef}
                className={styles.answerInput}
                value={typed}
                onChange={e => onTyped(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') checkEnding() }}
                placeholder="type the correct form…"
                spellCheck="false"
                autoComplete="off"
              />
              <div className={styles.endActions}>
                <button className={styles.checkBtn} onClick={checkEnding}>Check</button>
                {endAttempts >= 1 && (
                  <span className={styles.endHint}>💡 {item?.ending
                    ? `the ending changes — ${item.ending.length} letter${item.ending.length === 1 ? '' : 's'} (shown greyed above)`
                    : 'no change — same as the dictionary form'}</span>
                )}
                {!endRevealed && <button className={styles.linkBtn} onClick={() => setEndRevealed(true)}>reveal</button>}
              </div>
              {endRevealed && <div className={styles.revealNote}>Answer: <strong>{item?.answer}</strong></div>}
            </>
          )}

          {stage === 'done' && <div className={styles.doneMsg}>{caseAttempts === 0 && endAttempts === 0 ? '★ Perfect!' : '✓ Nice'}</div>}
        </div>

        <div className={styles.controls}>
          <button className={styles.sideBtn} onClick={hear} title="Hear the full sentence"><VolumeUpIcon /></button>
          <button className={styles.sideBtn} onClick={() => setShowRef(true)} title="Endings reference board — guides you to the right cell"><TableChartIcon /></button>
          <button className={styles.sideBtn} onClick={skip} title="Skip (resets streak)"><SkipNextIcon /></button>
        </div>
        <div className={styles.footRow}>
          <span className={styles.progress}>{idx + 1} / {items.length} · best {best}</span>
          <button className={styles.linkBtn} onClick={flagWrong} title="Skip a dubious exercise — keeps your streak">⚑ looks wrong</button>
        </div>
      </div>
      {showRef && <RefBoard item={item} onClose={() => setShowRef(false)} />}
    </div>
  )
}

// Guided endings reference: shows the full table, then after 2s highlights the
// learner's CASE, then after 2s more spotlights the exact gender/number cell and
// reveals the answer. "Reveal now" jumps straight to the spotlight.
function RefBoard({ item, onClose }) {
  const [phase, setPhase] = useState(0) // 0 all · 1 case highlighted · 2 cell + answer
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 2000)
    const t2 = setTimeout(() => setPhase(2), 4000)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [])
  const activeCase = item?.caseKey
  const matchSub = (subs) => item?.number === 'pl'
    ? subs.find(s => s.num === 'pl')
    : (subs.find(s => s.g && s.g.includes(item?.gender)) || subs[0])

  return (
    <div className={styles.refOverlay} onClick={onClose}>
      <div className={styles.refModal} onClick={e => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.modeLabel}>Endings reference</div>
        <div className={styles.refStatus}>
          {phase === 0 ? 'Find your case in the table…'
            : phase === 1 ? <>Your case: <strong>{CASE_BY_KEY[activeCase]?.label}</strong> — now the ending…</>
            : <>{item?.baseNoun} → <strong className={styles.refHi}>{item?.answer}</strong> ({CASE_BY_KEY[activeCase]?.label}, {item?.number === 'pl' ? 'plural' : ({ m: 'masculine', f: 'feminine', n: 'neuter' }[item?.gender] || item?.gender)})</>}
        </div>

        <div className={styles.refTable}>
          <div className={`${styles.refRow} ${styles.refHeadRow}`}>
            <div>g/n</div><div>Adjective</div><div>Noun</div><div>Examples</div>
          </div>
          {CASES.map(c => {
            const subs = DECL[c.key] || []
            const isActive = c.key === activeCase
            const target = isActive ? matchSub(subs) : null
            return (
              <div key={c.key} className={`${styles.refSection} ${phase >= 1 && isActive ? styles.refSectionActive : ''} ${phase >= 1 && !isActive ? styles.refDim : ''}`}>
                <div className={styles.refCaseLabel}>{c.label} · {c.ru}</div>
                {subs.map((s, i) => {
                  const isTarget = phase >= 2 && isActive && s === target
                  return (
                    <div key={i} className={`${styles.refRow} ${isTarget ? styles.refTarget : ''}`}>
                      <div className={styles.refGN}>{s.num === 'pl' ? 'pl.' : (s.g || []).join('/') + '.'}</div>
                      <div>{s.adj}</div>
                      <div className={styles.refNoun}>{s.noun}</div>
                      <div className={styles.refEx}>{s.ex}</div>
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>

        <div className={styles.refFoot}>
          {phase < 2
            ? <button className={styles.checkBtn} onClick={() => setPhase(2)}>Reveal my ending →</button>
            : <button className={styles.checkBtn} onClick={onClose}>Got it</button>}
        </div>
      </div>
    </div>
  )
}

// Tap-to-hear Hangul board — click any letter to hear its sound (played as a
// syllable) and see its romanization. Fully self-contained: no model, no network.
function HangulBoard({ voiceSpeed, onClose }) {
  const [last, setLast] = useState(null)
  const say = (jamo, syllable, e) => {
    if (e) popScale(e.currentTarget)
    setLast(jamo)
    speak(syllable, voices.kor || voices['eng'], voiceSpeed)
  }
  const tile = {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.1rem',
    background: '#141418', border: '1px solid #2a2a33', borderRadius: 12,
    padding: '0.7rem 0.4rem', cursor: 'pointer', color: '#e8e8ec', transition: 'transform .1s',
  }
  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(76px, 1fr))', gap: '0.55rem' }
  const heading = { fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.09em', color: '#8a8a95', margin: '0 0 0.6rem' }
  const section = (title, sub, items) => (
    <div style={{ marginBottom: '1.7rem' }} key={title}>
      <div style={heading}>{title} <span style={{ color: '#5a5a65' }}>· {sub}</span></div>
      <div style={grid}>
        {items.map(([j, r, syl]) => (
          <button key={j} onClick={(e) => say(j, syl, e)} style={{ ...tile, borderColor: last === j ? '#34d399' : '#2a2a33' }}>
            <span style={{ fontSize: '2rem', fontWeight: 600, lineHeight: 1 }}>{j}</span>
            <span style={{ fontSize: '0.78rem', color: '#6ee7b7', fontWeight: 500 }}>{r}</span>
            <span style={{ fontSize: '0.72rem', color: '#8a8a95' }}>{syl}</span>
          </button>
        ))}
      </div>
    </div>
  )
  return (
    <div className={styles.view}>
      <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
      <div style={{ maxWidth: 920, width: '100%', margin: '0 auto', padding: '2rem 1.5rem 3rem', height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
        <div className={styles.modeLabel}>Korean · Alphabet</div>
        <h1 className={styles.title} style={{ marginBottom: '0.25rem' }}>한글 — tap to hear</h1>
        <p className={styles.sub} style={{ marginBottom: '1.5rem' }}>Tap any letter to hear its sound. Consonants are played with ㅏ (a); vowels with the silent ㅇ.</p>
        {section('Consonants', '자음', HANGUL.consonants)}
        {section('Double consonants', '쌍자음', HANGUL.doubles)}
        {section('Vowels', '모음', HANGUL.vowels)}
        {section('Complex vowels', '복합 모음', HANGUL.complexVowels)}
      </div>
    </div>
  )
}
